import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useWorkspaceStore, setCurrentWorkspace, setWorkspaces } from '../store/workspaceStore';
import {
  useCollectionsStore,
  setCollections,
  setSelectedRequest,
  setSelectedCollection,
  setSelectedFolder,
  setEditingItem,
} from '../store/collectionsStore';
import {
  useTabsStore,
  setRequestTabs,
  setCollectionTabs,
  setActiveCollectionTabId,
  setActiveTabType,
} from '../store/tabsStore';
import {
  setEnvironments,
  setSelectedEnvironment,
  setWorkspaceVariables,
  setGlobalVariables,
} from '../store/variablesStore';
import { hydrateCollectionsFromApi } from '../utils/collectionFolders';
import { isBackendWorkspace, isSyncEngineWorkspace } from '../utils/workspaceStorage';
import { Presence } from 'phoenix';
import { isTauri } from '../utils/tauri';
import { usePresenceStore } from '../store/presenceStore';
import { getClientId } from '../utils/clientId';
import { collectionService } from '../services/collectionService';
import { enrichCloudCollectionsWithLinks } from '../services/localCollectionLink';
import {
  applyCollectionCreated,
  applyCollectionUpdated,
  applyCollectionDeleted,
  applyRequestCreated,
  applyRequestUpdated,
  applyRequestDeleted,
  applyCollectionVariables,
  applySidebarLayout,
} from '../utils/workspaceRealtime';
import {
  joinWorkspaceChannel,
  leaveWorkspaceChannel,
  addWorkspaceEventListener,
  WORKSPACE_REJOINED_EVENT,
} from '../services/workspaceChannelService';

const sameId = (a, b) => String(a) === String(b);

// A remotely deleted entity may be open in a tab. Mirror the local delete
// behavior (useCollections deleteRequest closes matching tabs) for saved tabs,
// but a tab with unsaved edits is work the receiver hasn't persisted — detach
// it into a new-request draft instead of destroying it. Its collectionId is
// kept: saving re-creates the request there, or prompts for a destination if
// that collection is gone too.
function closeOrDetachRequestTabs(matches) {
  setRequestTabs((prev) => {
    if (!prev.some(matches)) return prev;
    return prev
      .map((tab) => {
        if (!matches(tab)) return tab;
        if (tab.hasUnsavedChanges) {
          return { ...tab, originalId: null, isNewRequest: true };
        }
        return null;
      })
      .filter(Boolean);
  });
}

function reconcileAfterRequestDeleted(payload) {
  const id = payload?.request_id;
  if (id == null) return;
  closeOrDetachRequestTabs((tab) => sameId(tab.originalId, id) || sameId(tab.id, id));
  const { selectedRequest, editingItem } = useCollectionsStore.getState();
  if (selectedRequest && sameId(selectedRequest.id, id)) {
    setSelectedRequest(null);
  }
  if (editingItem?.type === 'request' && sameId(editingItem.id, id)) {
    setEditingItem(null);
  }
}

function reconcileAfterCollectionDeleted(payload) {
  const id = payload?.collection_id;
  if (id == null) return;

  // Close collection tabs for the deleted collection (mirrors local delete).
  const { collectionTabs, activeCollectionTabId } = useTabsStore.getState();
  const remaining = collectionTabs.filter((tab) => !sameId(tab.collectionId, id));
  if (remaining.length !== collectionTabs.length) {
    const closedActive = collectionTabs.some(
      (tab) => tab.id === activeCollectionTabId && sameId(tab.collectionId, id)
    );
    setCollectionTabs(remaining);
    if (closedActive) {
      if (remaining.length > 0) {
        const next = remaining[remaining.length - 1];
        setActiveCollectionTabId(next.id);
        setActiveTabType('collection');
        const col = useCollectionsStore
          .getState()
          .collections.find((c) => sameId(c.id, next.collectionId));
        setSelectedCollection(col || null);
      } else {
        setActiveCollectionTabId(null);
        setActiveTabType('request');
      }
    }
  }

  closeOrDetachRequestTabs((tab) => sameId(tab.collectionId, id));

  const { selectedCollection, selectedFolder, editingItem } = useCollectionsStore.getState();
  if (selectedCollection && sameId(selectedCollection.id, id)) {
    setSelectedCollection(null);
  }
  if (selectedFolder && sameId(selectedFolder.collectionId, id)) {
    setSelectedFolder(null);
  }
  if (editingItem?.type === 'collection' && sameId(editingItem.id, id)) {
    setEditingItem(null);
  }
}

// Workspace-scope variable broadcast carries the full authoritative scopes
// (globals + workspace_globals + environments). Apply them to the variables
// store and mirror into the workspace records so later hydrations and saves
// start from the fresh values. Environment selection is re-matched by id/name
// like useVariableHydration does.
function applyWorkspaceVariablesBroadcast(payload) {
  const scopes = payload?.variable_scopes;
  if (!scopes) return;
  setGlobalVariables(scopes.globals || []);
  setWorkspaceVariables(scopes.workspace_globals || []);
  const nextEnvironments = scopes.environments || [];
  setEnvironments(nextEnvironments);
  setSelectedEnvironment((prev) =>
    prev
      ? nextEnvironments.find((env) => env.id === prev.id || env.name === prev.name) || null
      : nextEnvironments[0] || null
  );

  const settings = payload.settings;
  const patch = (ws) => ({
    ...ws,
    variable_scopes: scopes,
    ...(settings ? { settings } : {}),
  });
  const currentId = useWorkspaceStore.getState().currentWorkspace?.id;
  if (currentId == null) return;
  setCurrentWorkspace((prev) => (prev && sameId(prev.id, currentId) ? patch(prev) : prev));
  setWorkspaces((prev) => prev.map((ws) => (sameId(ws.id, currentId) ? patch(ws) : ws)));
}

/**
 * Subscribes a team cloud workspace to the realtime channel and applies each
 * broadcast entity directly to local `collections` (no per-event refetch).
 * Self-originated echoes are skipped (Phoenix echoes to the sender). No-ops for
 * local / personal workspaces. Extracted verbatim from App to keep the shell thin.
 */
export function useWorkspaceRealtime() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const workspaceReloadNonce = useWorkspaceStore((s) => s.workspaceReloadNonce);

  useEffect(() => {
    if (!isAuthenticated || !currentWorkspace?.id) {
      leaveWorkspaceChannel();
      return undefined;
    }

    const isTeamCloudWorkspace =
      isBackendWorkspace(currentWorkspace, isAuthenticated) && currentWorkspace.type === 'team';

    if (!isTeamCloudWorkspace) {
      leaveWorkspaceChannel();
      return undefined;
    }

    joinWorkspaceChannel(currentWorkspace.id);

    // Presence (Phase 5): who's online, kept for both legacy and sync-engine
    // paths. State/diff frames flow through the wildcard onMessage forwarder.
    let presenceState = {};
    const presenceUnsubs = [
      addWorkspaceEventListener('presence_state', (payload) => {
        presenceState = Presence.syncState(presenceState, payload || {});
        usePresenceStore.getState().setPresenceState(presenceState);
      }),
      addWorkspaceEventListener('presence_diff', (payload) => {
        presenceState = Presence.syncDiff(presenceState, payload || {});
        usePresenceStore.getState().setPresenceState(presenceState);
      }),
    ];

    // Sync-engine-enrolled team workspace (Phase 5): the channel is
    // delivery-only. sync_delta entries feed the Rust engine (contiguous =>
    // applied + cursor advance; gap => engine pulls); the UI refresh rides
    // sync:remote-applied via useSyncEngine. Reconnects gap-fill with a pull
    // instead of the legacy full-collections refetch. Own echoes are harmless
    // (idempotent apply) and useful — they advance the cursor past our own
    // seqs. Legacy entity events are ignored on this path.
    if (isTauri() && isSyncEngineWorkspace(currentWorkspace, isAuthenticated)) {
      const workspaceKey = String(currentWorkspace.id);
      const invokeSync = async (cmd, args) => {
        try {
          const { invoke } = await import('@tauri-apps/api/core');
          return await invoke(cmd, args);
        } catch (err) {
          console.warn(`[sync] ${cmd} failed:`, err);
          return null;
        }
      };
      const unsubs = [
        ...presenceUnsubs,
        addWorkspaceEventListener('sync_delta', (payload) => {
          invokeSync('sync_apply_broadcast', {
            workspaceId: workspaceKey,
            changes: payload?.changes || [],
          });
        }),
        addWorkspaceEventListener(WORKSPACE_REJOINED_EVENT, () => {
          invokeSync('sync_pull_now', { workspaceId: workspaceKey });
        }),
      ];
      return () => {
        unsubs.forEach((unsub) => unsub());
        usePresenceStore.getState().clearPresence();
        leaveWorkspaceChannel();
      };
    }

    // Each broadcast carries the changed entity, so apply it directly to local
    // state instead of refetching the whole tree per event. This keeps remote
    // edits instant and silent (no toast, no GET storm). Collection-scoped
    // variable edits flow through automatically because collectionVariablesById
    // re-derives from `collections`.
    // Phoenix's Endpoint.broadcast echoes to the sender too, so the user who
    // made a change also receives their own event. They already applied it
    // locally (optimistic update), so applying the echo would double it (e.g.
    // duplicating a collection races the echo against the optimistic append).
    // Skip self-originated events; other members' events still flow through.
    // "Self" is the DEVICE, not the user: up to two devices may share one
    // account, and each must receive the other's edits. Payloads carry the
    // originating device's client id (X-Client-Id echoed back as
    // actor.client_id). Non-browser writers (e.g. the CLI) never send a
    // client id, so a missing actor.client_id means "not us" — falling back
    // to a user-id match would treat every CLI edit as this device's own
    // echo and silently drop it.
    const ownClientId = getClientId();
    const fromSelf = (payload) => {
      const actor = payload?.actor;
      if (actor?.client_id == null || !ownClientId) return false;
      return String(actor.client_id) === String(ownClientId);
    };

    // Newly created entities have no per-entity shares yet, so the receiver's
    // workspace-level edit right is the correct can_edit for them.
    const receiverCanEdit = currentWorkspace.can_edit;
    const onCollections = (apply) => (payload) => {
      if (fromSelf(payload)) return;
      setCollections((prev) => apply(prev, payload));
    };
    const onCreate = (apply) => (payload) => {
      if (fromSelf(payload)) return;
      setCollections((prev) => apply(prev, payload, receiverCanEdit));
    };

    // Broadcasts sent while the socket was disconnected are never replayed, so
    // after a rejoin the local tree may have silently diverged. Refetch the
    // receiver-scoped collections index (same call the loader uses) to resync.
    const workspaceId = currentWorkspace.id;
    // A flapping connection can fire the rejoin event faster than the fetch
    // resolves; without a guard, overlapping GETs race and the response that
    // lands last (not necessarily the one for the latest rejoin) wins.
    let resyncInFlight = false;
    const resyncAfterRejoin = async () => {
      if (resyncInFlight) return;
      resyncInFlight = true;
      try {
        const apiCollections = await collectionService.getCollections(workspaceId);
        // The workspace may have changed while the fetch was in flight.
        if (!sameId(useWorkspaceStore.getState().currentWorkspace?.id, workspaceId)) return;
        const deviceList = useWorkspaceStore.getState().deviceLocalCollections || [];
        setCollections(
          enrichCloudCollectionsWithLinks(hydrateCollectionsFromApi(apiCollections), deviceList)
        );
      } catch (err) {
        console.warn('Collections resync after channel rejoin failed:', err);
      } finally {
        resyncInFlight = false;
      }
    };

    const unsubs = [
      addWorkspaceEventListener('collection_created', onCreate(applyCollectionCreated)),
      addWorkspaceEventListener('collection_updated', onCollections(applyCollectionUpdated)),
      addWorkspaceEventListener('collection_deleted', (payload) => {
        if (fromSelf(payload)) return;
        setCollections((prev) => applyCollectionDeleted(prev, payload));
        reconcileAfterCollectionDeleted(payload);
      }),
      addWorkspaceEventListener('request_created', onCreate(applyRequestCreated)),
      addWorkspaceEventListener('request_updated', onCollections(applyRequestUpdated)),
      addWorkspaceEventListener('request_deleted', (payload) => {
        if (fromSelf(payload)) return;
        setCollections((prev) => applyRequestDeleted(prev, payload));
        reconcileAfterRequestDeleted(payload);
      }),
      // The layout payload is actor-scoped (their access filter, their
      // can_edit), so it is merged onto local state, never swapped in whole.
      addWorkspaceEventListener('sidebar_layout_updated', (payload) => {
        if (fromSelf(payload)) return;
        setCollections((prev) => applySidebarLayout(prev, payload, receiverCanEdit));
      }),
      addWorkspaceEventListener('variables_updated', (payload) => {
        if (fromSelf(payload)) return;
        if (payload?.scope === 'collection') {
          setCollections((prev) => applyCollectionVariables(prev, payload));
        } else if (payload?.scope === 'workspace') {
          applyWorkspaceVariablesBroadcast(payload);
        }
      }),
      addWorkspaceEventListener(WORKSPACE_REJOINED_EVENT, () => {
        resyncAfterRejoin();
      }),
    ];

    return () => {
      unsubs.forEach((unsub) => unsub());
      presenceUnsubs.forEach((unsub) => unsub());
      usePresenceStore.getState().clearPresence();
      leaveWorkspaceChannel();
    };
  }, [currentWorkspace?.id, currentWorkspace?.type, currentWorkspace?.can_edit, user?.id, isAuthenticated, workspaceReloadNonce]); // eslint-disable-line react-hooks/exhaustive-deps
}
